import {
  ViewportLeaderLineDomRenderer,
  type ViewportLeaderSegment,
} from '@/core/viewport-overlay';
import type { ProjectedBlendPreviewGuide } from './types';

type BlendPreviewGuideDomRendererOptions = {
  /** 控制点点击回调。 */
  onControlPointSelect?: (key: string) => void;
};

/**
 * 圆角/倒角预览引导 DOM 渲染器。
 *
 * 设计说明：
 * 1. 预览引导是纯 UI 表达，不应和 cad-core 的几何求解或 Vue 组件模板耦合；
 * 2. 引线渲染委托给通用 ViewportLeaderLineDomRenderer，本类只负责气泡与控制点；
 * 3. 控制器负责数据驱动与逐帧刷新，渲染器只做幂等视图输出，职责单一。
 */
export class BlendPreviewGuideDomRenderer {
  /** 控制点点击回调。 */
  private readonly onControlPointSelect?: (key: string) => void;
  /** 通用引线渲染器。 */
  private readonly leaderLineRenderer: ViewportLeaderLineDomRenderer;
  /** 根节点。 */
  private rootElement: HTMLDivElement | null = null;
  /** 标签图层。 */
  private itemsElement: HTMLDivElement | null = null;
  /** 已创建的气泡节点，按引导 ID 复用，避免逐帧重建 DOM。 */
  private readonly balloonElements = new Map<string, HTMLDivElement>();
  /** 已创建的控制点节点，按引导 ID 复用，避免逐帧重建 DOM。 */
  private readonly controlPointElements = new Map<string, HTMLButtonElement>();
  /** 当前帧有效引导 ID 集合，复用集合对象以减少热路径分配。 */
  private readonly activeGuideIds = new Set<string>();

  /**
   * @param options DOM 渲染器参数。
   */
  public constructor(options: BlendPreviewGuideDomRendererOptions = {}) {
    this.onControlPointSelect = options.onControlPointSelect;
    this.leaderLineRenderer = new ViewportLeaderLineDomRenderer({ zIndex: 12 });
  }

  /**
   * 渲染当前引导集合。
   *
   * @param guides 已投影引导集合。
   * @param container 视口容器。
   */
  public render(
    guides: ProjectedBlendPreviewGuide[],
    container: HTMLElement | null,
  ): void {
    if (!container || guides.length === 0) {
      this.clear();
      return;
    }

    const rootElement = this.ensureRoot(container);
    const itemsElement = this.ensureItemsLayer(rootElement);
    this.leaderLineRenderer.sync(this.buildLeaderSegments(guides), container);

    this.activeGuideIds.clear();
    for (const guide of guides) {
      this.activeGuideIds.add(guide.id);
      if (guide.kind === 'control-point') {
        this.updateControlPoint(this.ensureControlPoint(guide.id, itemsElement, guide), guide);
        continue;
      }
      this.updateBalloon(this.ensureBalloon(guide.id, itemsElement), guide);
    }
    this.removeInactiveElements(this.balloonElements, this.activeGuideIds);
    this.removeInactiveElements(this.controlPointElements, this.activeGuideIds);
  }

  /**
   * 清空渲染内容。
   */
  public clear(): void {
    this.leaderLineRenderer.clear();
    if (this.rootElement?.parentElement) {
      this.rootElement.parentElement.removeChild(this.rootElement);
    }
    this.rootElement = null;
    this.itemsElement = null;
    this.balloonElements.clear();
    this.controlPointElements.clear();
    this.activeGuideIds.clear();
  }

  /**
   * 释放渲染器。
   */
  public dispose(): void {
    this.clear();
    this.leaderLineRenderer.dispose();
  }

  /**
   * 将已投影引导转换为引线段集合。
   *
   * @param guides 已投影引导集合。
   * @returns 引线段集合。
   */
  private buildLeaderSegments(guides: ProjectedBlendPreviewGuide[]): ViewportLeaderSegment[] {
    const segments: ViewportLeaderSegment[] = [];
    for (const guide of guides) {
      if (guide.kind === 'control-point') {
        continue;
      }
      const leaderAnchors = guide.leaderAnchors && guide.leaderAnchors.length > 0
        ? guide.leaderAnchors
        : [guide.anchor];
      leaderAnchors.forEach((leaderAnchor, index) => {
        segments.push({
          id: `${guide.id}:leader:${index}`,
          from: leaderAnchor,
          to: guide.label,
          anchorMarker: guide.anchorMarker === 'dot' ? 'dot' : 'none',
        });
      });
    }
    return segments;
  }

  /**
   * 确保根节点存在。
   *
   * @param container 视口容器。
   * @returns 根节点。
   */
  private ensureRoot(container: HTMLElement): HTMLDivElement {
    if (this.rootElement && this.rootElement.parentElement === container) {
      return this.rootElement;
    }

    this.clear();
    const rootElement = document.createElement('div');
    rootElement.style.position = 'absolute';
    rootElement.style.inset = '0';
    rootElement.style.pointerEvents = 'none';
    rootElement.style.zIndex = '12';
    container.appendChild(rootElement);
    this.rootElement = rootElement;
    return rootElement;
  }

  /**
   * 确保标签图层存在。
   *
   * @param rootElement 根节点。
   * @returns 标签图层。
   */
  private ensureItemsLayer(rootElement: HTMLDivElement): HTMLDivElement {
    if (this.itemsElement && this.itemsElement.parentElement === rootElement) {
      return this.itemsElement;
    }

    const itemsElement = document.createElement('div');
    itemsElement.style.position = 'absolute';
    itemsElement.style.inset = '0';
    itemsElement.style.pointerEvents = 'none';
    rootElement.appendChild(itemsElement);
    this.itemsElement = itemsElement;
    return itemsElement;
  }

  /**
   * 创建气泡标签。
   *
   * @param guide 已投影引导。
   * @returns 标签节点。
   */
  private ensureBalloon(id: string, itemsElement: HTMLDivElement): HTMLDivElement {
    const existingElement = this.balloonElements.get(id);
    if (existingElement && existingElement.parentElement === itemsElement) {
      return existingElement;
    }

    const balloonElement = document.createElement('div');
    balloonElement.style.position = 'absolute';
    balloonElement.style.transform = 'translate(-50%, -50%)';
    balloonElement.style.padding = '2px 6px';
    balloonElement.style.border = '1px solid #111827';
    balloonElement.style.background = '#ffffff';
    balloonElement.style.color = '#111827';
    balloonElement.style.fontSize = '12px';
    balloonElement.style.lineHeight = '1.4';
    balloonElement.style.whiteSpace = 'pre';
    itemsElement.appendChild(balloonElement);
    this.balloonElements.set(id, balloonElement);
    return balloonElement;
  }

  /**
   * 创建变量圆角实例控制点。
   *
   * @param id 引导 ID。
   * @param itemsElement 标签图层。
   * @param guide 已投影引导。
   * @returns 控制点节点。
   */
  private ensureControlPoint(
    id: string,
    itemsElement: HTMLDivElement,
    guide: ProjectedBlendPreviewGuide,
  ): HTMLButtonElement {
    const existingElement = this.controlPointElements.get(id);
    if (existingElement && existingElement.parentElement === itemsElement) {
      return existingElement;
    }

    const controlPointElement = document.createElement('button');
    controlPointElement.type = 'button';
    controlPointElement.style.position = 'absolute';
    controlPointElement.style.width = '9px';
    controlPointElement.style.height = '9px';
    controlPointElement.style.padding = '0';
    controlPointElement.style.border = '1px solid #111827';
    controlPointElement.style.borderRadius = '50%';
    controlPointElement.style.background = '#facc15';
    controlPointElement.style.transform = 'translate(-50%, -50%)';
    controlPointElement.style.pointerEvents = 'auto';
    controlPointElement.style.cursor = 'pointer';
    controlPointElement.style.boxSizing = 'border-box';
    controlPointElement.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      /**
       * 控制点 DOM 节点按 id 复用，不能在事件闭包里捕获创建时的 guide。
       * 已设置的 P 点会从候选 I# 切换为真实 P# key，点击时必须读取最新 dataset，
       * 否则会继续按旧候选点 upsert，导致列表点击/场景点击后错误新增 P2。
       */
      const key = controlPointElement.dataset.guideKey;
      if (key) {
        this.onControlPointSelect?.(key);
      }
    });
    itemsElement.appendChild(controlPointElement);
    this.controlPointElements.set(id, controlPointElement);
    return controlPointElement;
  }

  /**
   * 更新气泡位置与文本。
   *
   * @param balloonElement 气泡节点。
   * @param guide 已投影引导。
   */
  private updateBalloon(balloonElement: HTMLDivElement, guide: ProjectedBlendPreviewGuide): void {
    balloonElement.style.left = `${guide.label.x}px`;
    balloonElement.style.top = `${guide.label.y}px`;
    if (guide.fields && guide.fields.length > 0) {
      balloonElement.textContent = guide.fields
        .map((field) => `${field.label} ${field.valueText}`)
        .join('\n');
      return;
    }
    balloonElement.textContent = guide.title
      ? `${guide.title} ${guide.valueText}`
      : guide.valueText;
  }

  /**
   * 更新变量圆角实例控制点位置。
   *
   * @param controlPointElement 控制点节点。
   * @param guide 已投影引导。
   */
  private updateControlPoint(
    controlPointElement: HTMLButtonElement,
    guide: ProjectedBlendPreviewGuide,
  ): void {
    controlPointElement.style.left = `${guide.anchor.x}px`;
    controlPointElement.style.top = `${guide.anchor.y}px`;
    controlPointElement.title = guide.title;
    controlPointElement.dataset.guideKey = guide.key ?? '';
    controlPointElement.dataset.guideState = guide.state ?? 'candidate';
    controlPointElement.style.background = guide.state === 'assigned' ? '#2f8cff' : '#facc15';
    controlPointElement.style.borderColor = guide.state === 'assigned' ? '#0f172a' : '#111827';
  }

  /**
   * 移除当前帧不再可见的 DOM 节点。
   *
   * @param elements DOM 节点索引。
   * @param activeIds 当前帧有效 ID。
   */
  private removeInactiveElements<TElement extends Element>(
    elements: Map<string, TElement>,
    activeIds: ReadonlySet<string>,
  ): void {
    for (const [id, element] of elements) {
      if (activeIds.has(id)) {
        continue;
      }
      element.remove();
      elements.delete(id);
    }
  }
}
